import { useState, useCallback, useEffect, useMemo } from 'react'
import { sumMoney } from '@/lib/money'
import { useAuth } from '@/contexts/AuthContext'
import { SalarySnapshotService } from '@/services/salarySnapshotService'
import { PeriodService } from '@/services/periodService'
import { CompleteSalarySnapshot } from '@/types/salarySnapshot'
import { useConfigStore } from '@/stores/configStore'
import { FleetSettings } from '@/types'

/**
 * Represents a potential duplicate pair of drivers
 */
export interface DuplicatePair {
  driver1Id: string
  driver1Name: string
  driver2Id: string
  driver2Name: string
  reason: 'similar_name' | 'same_earnings'
  similarity?: number
  details: string
}

/**
 * Period totals computed from snapshots
 */
export interface PeriodTotals {
  totalGrossSalary: number
  totalAmountToPay: number
  totalEmployerCost: number
  driverCount: number
  includedCount: number
}

/**
 * Review step state
 */
export interface WizardReviewState {
  isLoading: boolean
  error: string | null

  // Data
  snapshots: CompleteSalarySnapshot[]
  fleetSettings: FleetSettings | null

  // Computed
  periodTotals: PeriodTotals
  potentialDuplicates: DuplicatePair[]

  // UI state
  expandedDriverIds: Set<string>
  excludedDriverIds: Set<string>
  showSettingsDetails: boolean
  showDuplicateDetails: boolean
}

const initialState: WizardReviewState = {
  isLoading: true,
  error: null,
  snapshots: [],
  fleetSettings: null,
  periodTotals: {
    totalGrossSalary: 0,
    totalAmountToPay: 0,
    totalEmployerCost: 0,
    driverCount: 0,
    includedCount: 0
  },
  potentialDuplicates: [],
  expandedDriverIds: new Set(),
  excludedDriverIds: new Set(),
  showSettingsDetails: false,
  showDuplicateDetails: false
}

export interface WizardReviewController {
  state: WizardReviewState

  // Data actions
  reloadData: () => Promise<void>

  // Exclusion actions
  toggleDriverExclusion: (driverId: string) => void
  setDriverExcluded: (driverId: string, excluded: boolean) => void
  saveExclusions: () => Promise<void>

  // UI actions
  toggleDriverExpanded: (driverId: string) => void
  toggleSettingsDetails: () => void
  toggleDuplicateDetails: () => void
}

/**
 * Name normalization for fuzzy matching
 */
function normalizeNameForMatching(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-zåäö\s]/g, '')
}

function tokenizeName(name: string): string[] {
  return normalizeNameForMatching(name)
    .split(' ')
    .filter(token => token.length > 0)
}

function extractFirstLastName(name: string): string {
  const tokens = tokenizeName(name)
  if (tokens.length === 0) return ''
  if (tokens.length === 1) return tokens[0]
  if (tokens.length === 2) return tokens.join(' ')
  return `${tokens[0]} ${tokens[tokens.length - 1]}`
}

function calculateJaccardSimilarity(tokens1: string[], tokens2: string[]): number {
  const set1 = new Set(tokens1)
  const set2 = new Set(tokens2)
  const intersection = new Set([...set1].filter(x => set2.has(x)))
  const union = new Set([...set1, ...set2])
  return union.size === 0 ? 0 : intersection.size / union.size
}

/**
 * Detect potential duplicate drivers based on name similarity
 */
function detectDuplicates(snapshots: CompleteSalarySnapshot[]): DuplicatePair[] {
  const duplicates: DuplicatePair[] = []
  const JACCARD_THRESHOLD = 0.66

  for (let i = 0; i < snapshots.length; i++) {
    for (let j = i + 1; j < snapshots.length; j++) {
      const snap1 = snapshots[i]
      const snap2 = snapshots[j]

      const name1 = snap1.driverInfo.name
      const name2 = snap2.driverInfo.name

      // Check first+last name match
      const firstLast1 = extractFirstLastName(name1)
      const firstLast2 = extractFirstLastName(name2)

      if (firstLast1 === firstLast2 && firstLast1.length > 0) {
        duplicates.push({
          driver1Id: snap1.driverId,
          driver1Name: name1,
          driver2Id: snap2.driverId,
          driver2Name: name2,
          reason: 'similar_name',
          similarity: 100,
          details: `Samma för- och efternamn: "${firstLast1}"`
        })
        continue
      }

      // Check Jaccard similarity
      const tokens1 = tokenizeName(name1)
      const tokens2 = tokenizeName(name2)
      const similarity = calculateJaccardSimilarity(tokens1, tokens2)

      if (similarity >= JACCARD_THRESHOLD) {
        duplicates.push({
          driver1Id: snap1.driverId,
          driver1Name: name1,
          driver2Id: snap2.driverId,
          driver2Name: name2,
          reason: 'similar_name',
          similarity: Math.round(similarity * 100),
          details: `Namnlikhet: ${Math.round(similarity * 100)}%`
        })
      }
    }
  }

  return duplicates
}

/**
 * Controller hook for the Review step
 */
export function useWizardReviewController(periodId: string): WizardReviewController {
  const { currentUser } = useAuth()
  const { settings: fleetSettings } = useConfigStore()
  const [state, setState] = useState<WizardReviewState>({
    ...initialState,
    fleetSettings
  })

  const updateState = useCallback((updates: Partial<WizardReviewState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  // Load all data
  const loadData = useCallback(async () => {
    if (!currentUser) return

    updateState({ isLoading: true, error: null })

    try {
      // Load period to get excluded driver IDs
      const period = await PeriodService.getPeriod(currentUser, periodId)
      const excludedFromPeriod = new Set(period?.excludedDriverIds || [])

      // Create snapshots for all drivers in the period
      const snapshots = await SalarySnapshotService.createSnapshotsForPeriod(
        currentUser,
        periodId
      )

      // Detect duplicates
      const potentialDuplicates = detectDuplicates(snapshots)

      updateState({
        snapshots,
        fleetSettings,
        potentialDuplicates,
        excludedDriverIds: excludedFromPeriod,
        isLoading: false
      })
    } catch (error) {
      console.error('[WizardReview] Failed to load data:', error)
      updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Kunde inte ladda data'
      })
    }
  }, [currentUser, periodId, fleetSettings, updateState])

  // Compute period totals
  const periodTotals = useMemo((): PeriodTotals => {
    const { snapshots, excludedDriverIds } = state

    const included = snapshots.filter(s => !excludedDriverIds.has(s.driverId))

    return {
      totalGrossSalary: sumMoney(included.map(s => s.totals.finalGrossSalary)),
      totalAmountToPay: sumMoney(included.map(s => s.totals.amountToPay)),
      totalEmployerCost: sumMoney(included.map(s => s.totals.totalEmployerCost)),
      driverCount: snapshots.length,
      includedCount: included.length
    }
  }, [state.snapshots, state.excludedDriverIds])

  // Update state with computed totals
  useEffect(() => {
    setState(prev => ({ ...prev, periodTotals }))
  }, [periodTotals])

  // Load data on mount
  useEffect(() => {
    loadData()
  }, [loadData])

  // Reload data
  const reloadData = useCallback(async () => {
    await loadData()
  }, [loadData])

  // Toggle driver exclusion
  const toggleDriverExclusion = useCallback((driverId: string) => {
    setState(prev => {
      const newExcluded = new Set(prev.excludedDriverIds)
      if (newExcluded.has(driverId)) {
        newExcluded.delete(driverId)
      } else {
        newExcluded.add(driverId)
      }
      return { ...prev, excludedDriverIds: newExcluded }
    })
  }, [])

  // Set driver excluded status
  const setDriverExcluded = useCallback((driverId: string, excluded: boolean) => {
    setState(prev => {
      const newExcluded = new Set(prev.excludedDriverIds)
      if (excluded) {
        newExcluded.add(driverId)
      } else {
        newExcluded.delete(driverId)
      }
      return { ...prev, excludedDriverIds: newExcluded }
    })
  }, [])

  // Save exclusions to period document
  const saveExclusions = useCallback(async () => {
    if (!currentUser) return

    try {
      await PeriodService.updateExcludedDrivers(
        currentUser,
        periodId,
        Array.from(state.excludedDriverIds)
      )
    } catch (error) {
      console.error('[WizardReview] Failed to save exclusions:', error)
      throw error
    }
  }, [currentUser, periodId, state.excludedDriverIds])

  // UI actions
  const toggleDriverExpanded = useCallback((driverId: string) => {
    setState(prev => {
      const newExpanded = new Set(prev.expandedDriverIds)
      if (newExpanded.has(driverId)) {
        newExpanded.delete(driverId)
      } else {
        newExpanded.add(driverId)
      }
      return { ...prev, expandedDriverIds: newExpanded }
    })
  }, [])

  const toggleSettingsDetails = useCallback(() => {
    setState(prev => ({ ...prev, showSettingsDetails: !prev.showSettingsDetails }))
  }, [])

  const toggleDuplicateDetails = useCallback(() => {
    setState(prev => ({ ...prev, showDuplicateDetails: !prev.showDuplicateDetails }))
  }, [])

  return useMemo(() => ({
    state: { ...state, periodTotals },
    reloadData,
    toggleDriverExclusion,
    setDriverExcluded,
    saveExclusions,
    toggleDriverExpanded,
    toggleSettingsDetails,
    toggleDuplicateDetails
  }), [
    state,
    periodTotals,
    reloadData,
    toggleDriverExclusion,
    setDriverExcluded,
    saveExclusions,
    toggleDriverExpanded,
    toggleSettingsDetails,
    toggleDuplicateDetails
  ])
}
